import { AuthStorage } from "utils/auth-storage";
import { Button, message, Spin, Input, Popconfirm, Breadcrumb } from "antd";
import { HilyteService } from "apis/Services/HilyteService";
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import styles from "./pdfViewer.module.css";
import { Alert, Collapse, Steps } from "antd";
import {
  InfoCircleOutlined,
  QuestionCircleOutlined,
  ScissorOutlined,
  RobotOutlined,
  AimOutlined,
} from "@ant-design/icons";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DividePDF = ({
  pdfID,
  onBack,
  setShowPreview,
  setShowDividePDF,
  setFileUrl,
}) => {
  const { company_id, id } = useParams();
  const [fileURL, setFileURL] = useState(null);
  const [fileSections, setFileSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [existingSubmittals, setExistingSubmittals] = useState({});
  // Section dividers state
  const [dividers, setDividers] = useState([]);
  const [editingDivider, setEditingDivider] = useState(null);
  const [editingName, setEditingName] = useState("");

  // Panning States
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const pdfContainerRef = useRef(null);
  const pageRef = useRef(null);
  const dividerRefs = useRef({}); // Store refs to divider elements

  const baseWidth = 850;
  const pageWidth = baseWidth * zoomLevel;

  // Maximum width for dividers to prevent them from scaling too large
  const maxDividerWidth = 850;

  // Helper function to normalize category names for grouping
  const normalizeCategory = (category) => {
    // Extract the main category prefix (e.g., "Architectural" from "Architectural - Floor Plans")
    const match = category.match(/^([^-]+)/);
    return match ? match[1].trim() : category;
  };

  // Generate suggested dividers based on category changes
  const generateSuggestedDividers = (sections) => {
    if (!sections || sections.length === 0) return [];

    const suggested = [];
    let currentMainCategory = null;

    sections.forEach((section, index) => {
      const mainCategory = normalizeCategory(section.category);

      // Check if we're moving to a new main category
      if (currentMainCategory && mainCategory !== currentMainCategory) {
        // Add divider at the START of the new section
        suggested.push({
          id: `divider-${suggested.length}`,
          beforePage: section.page,
          name: mainCategory,
          isCustom: false,
        });
      } else if (index === 0) {
        // Add divider for the very first section
        suggested.push({
          id: `divider-${suggested.length}`,
          beforePage: section.page,
          name: mainCategory,
          isCustom: false,
        });
      }

      currentMainCategory = mainCategory;
    });

    return suggested;
  };

  const getPDF = async () => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    try {
      setLoading(true);
      const res = await HilyteService.getPDF({
        company: company_id,
        loginEmail,
        code,
        pdfID,
      });

      if (res?.presigned_url) {
        setFileURL(res.presigned_url);

        // For the preview component
        setFileUrl(res.presigned_url);
      } else {
        message.error("Failed to load PDF URL");
      }

      if (res?.json_submittals) {
        setExistingSubmittals(res.json_submittals);
      }

      if (res?.json_sections) {
        // If PDF was already divided, prefer divided sections
        if (
          res.json_sections.divided &&
          Array.isArray(res.json_sections.divided)
        ) {
          setFileSections(res.json_sections.original || []);

          // Build dividers directly from divided sections
          const restoredDividers = res.json_sections.divided.map(
            (section, index) => ({
              id: `divider-restored-${index}`,
              beforePage: section.startPage,
              name: section.name,
              isCustom: false,
            }),
          );

          setDividers(restoredDividers);
        } else if (Array.isArray(res.json_sections)) {
          // Undivided PDF – original flat sections list
          setFileSections(res.json_sections);

          const suggested = generateSuggestedDividers(res.json_sections);
          setDividers(suggested);
        } else {
          message.error("Invalid PDF sections format");
        }
      } else {
        message.error("Failed to load PDF Sections");
      }
    } catch (err) {
      console.error("Error fetching PDF:", err);
      message.error("Failed to load PDF");
      setError("Failed to load PDF");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pdfID) {
      getPDF();
    }
  }, [pdfID]);

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!pdfContainerRef.current || dividers.length === 0) return;

      const containerTop = pdfContainerRef.current.scrollTop;
      const sortedDividers = [...dividers].sort(
        (a, b) => a.beforePage - b.beforePage,
      );

      // Find which section we're currently viewing
      for (let i = sortedDividers.length - 1; i >= 0; i--) {
        const dividerEl = dividerRefs.current[sortedDividers[i].id];
        if (dividerEl) {
          const dividerTop =
            dividerEl.offsetTop - pdfContainerRef.current.offsetTop;
          if (containerTop >= dividerTop - 100) {
            setActiveSection(i);
            return;
          }
        }
      }
      setActiveSection(0);
    };

    const container = pdfContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [dividers]);

  function onLoadSuccess({ numPages }) {
    console.log("PDF loaded successfully with", numPages, "pages");
    setNumPages(numPages);
    setError(null);
  }

  function onLoadError(error) {
    console.error("Error loading PDF:", error);
    setError("Failed to load PDF. Please try again.");
    message.error("Failed to load PDF");
  }

  function onPageLoadSuccess(page) {
    setPageLoading(false);
  }

  function handleZoomIn() {
    setZoomLevel((prev) => Math.min(prev + 0.25, 4));
  }

  function handleZoomOut() {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  }

  function handleMouseDown(e) {
    // Don't start dragging if clicking on a divider control
    if (e.target.closest("[data-divider-control]")) {
      return;
    }
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX + pdfContainerRef.current.scrollLeft,
        y: e.clientY + pdfContainerRef.current.scrollTop,
      });
      e.preventDefault();
    }
  }

  function handleMouseMove(e) {
    if (isDragging && pdfContainerRef.current) {
      const dx = dragStart.x - e.clientX;
      const dy = dragStart.y - e.clientY;
      pdfContainerRef.current.scrollLeft = dx;
      pdfContainerRef.current.scrollTop = dy;
    }
  }

  function handleMouseUp() {
    setIsDragging(false);
  }

  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  // Divider management functions
  const handleAddDivider = (pageNumber) => {
    const newDivider = {
      id: `divider-custom-${Date.now()}`,
      beforePage: pageNumber,
      name: `Section ${dividers.length + 1}`,
      isCustom: true,
    };
    const updatedDividers = [...dividers, newDivider].sort(
      (a, b) => a.beforePage - b.beforePage,
    );
    setDividers(updatedDividers);
    message.success("Divider added");
  };

  const handleRemoveDivider = (dividerId) => {
    setDividers(dividers.filter((d) => d.id !== dividerId));
    message.success("Divider removed");
  };

  const handleEditDivider = (divider) => {
    setEditingDivider(divider.id);
    setEditingName(divider.name);
  };

  const handleSaveEdit = (dividerId) => {
    setDividers(
      dividers.map((d) =>
        d.id === dividerId ? { ...d, name: editingName } : d,
      ),
    );
    setEditingDivider(null);
    setEditingName("");
    message.success("Section name updated");
  };

  const handleCancelEdit = () => {
    setEditingDivider(null);
    setEditingName("");
  };

  const getDividerForPage = (pageNumber) => {
    return dividers.find((d) => d.beforePage === pageNumber);
  };

  // Build sections from dividers for display in sidebar
  const getSectionsFromDividers = () => {
    if (!numPages || dividers.length === 0) return [];

    const sortedDividers = [...dividers].sort(
      (a, b) => a.beforePage - b.beforePage,
    );

    const sections = sortedDividers.map((divider, index) => {
      const startPage = divider.beforePage;
      const endPage =
        index < sortedDividers.length - 1
          ? sortedDividers[index + 1].beforePage - 1
          : numPages;

      // Get pages in this section
      const pagesInSection = fileSections.filter(
        (fs) => fs.page >= startPage && fs.page <= endPage,
      );

      return {
        name: divider.name,
        startPage,
        endPage,
        pages: pagesInSection,
        dividerId: divider.id,
      };
    });

    return sections;
  };

  // Scroll to section when clicked
  const handleSectionClick = (section) => {
    const dividerEl = dividerRefs.current[section.dividerId];
    if (dividerEl && pdfContainerRef.current) {
      const containerTop = pdfContainerRef.current.offsetTop;
      const dividerTop = dividerEl.offsetTop - containerTop;

      pdfContainerRef.current.scrollTo({
        top: dividerTop - 20,
        behavior: "smooth",
      });
    }
  };

  const handleConfirmDividers = async () => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    // Sort dividers and create new sections
    const sortedDividers = [...dividers].sort(
      (a, b) => a.beforePage - b.beforePage,
    );

    const newSections = sortedDividers.map((divider, index) => {
      const startPage = divider.beforePage;
      const endPage =
        index < sortedDividers.length - 1
          ? sortedDividers[index + 1].beforePage - 1
          : numPages;

      return {
        name: divider.name,
        startPage: startPage,
        endPage: endPage,
        pageCount: endPage - startPage + 1,
      };
    });

    try {
      await HilyteService.updatePDFInfo({
        company: company_id,
        loginEmail,
        code,
        pdfID,
        sections: {
          original: fileSections,
          divided: newSections,
        },
        submittals: existingSubmittals,
      });

      message.success(`${newSections.length} sections saved successfully`);
      setShowPreview(true);
      setShowDividePDF(false);
    } catch (err) {
      console.error("Failed to update PDF info:", err);
      message.error("Failed to save sections");
    }
  };

  const containerCursor = isDragging
    ? "grabbing"
    : zoomLevel > 1
      ? "grab"
      : "default";

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 16,
        }}
      >
        <Spin size="large" />
        <div>Loading PDF...</div>
      </div>
    );
  }

  const sectionsData = getSectionsFromDividers();

  return (
    <div className={styles.container}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item>
            <a onClick={onBack}>Your Uploaded Files</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Review</Breadcrumb.Item>
        </Breadcrumb>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ color: "#666", fontSize: 14 }}>
            {dividers.length} section{dividers.length !== 1 ? "s" : ""} defined
          </span>
          <Button type="primary" onClick={handleConfirmDividers}>
            Confirm Sections
          </Button>
        </div>
      </div>

      {error && (
        <div style={{ color: "red", padding: "10px", textAlign: "center" }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <Alert
          type="info"
          // showIcon
          icon={<RobotOutlined style={{ fontSize: 18 }} />}
          message={
            <span style={{ fontWeight: 600, fontSize: 15 }}>
              Why are we dividing this PDF?
            </span>
          }
          description={
            <span style={{ fontSize: 13, color: "#444", lineHeight: "1.6" }}>
              Our AI analyzes this drawing set to automatically extract
              submittals — materials, products, and specifications your project
              requires. Because drawing sets can be hundreds of pages long, the
              AI needs the document split into logical sections (e.g.{" "}
              <em>Architectural</em>, <em>Electrical</em>, <em>Mechanical</em>)
              to work accurately and efficiently.
              <br />
              <strong>
                Inaccurate sections will directly reduce the quality of
                extracted submittals.
              </strong>{" "}
              Please review the suggested dividers carefully before confirming.
            </span>
          }
          style={{ marginBottom: 12, borderRadius: 8 }}
        />

        <Collapse
          ghost
          size="small"
          items={[
            {
              key: "instructions",
              label: (
                <span
                  style={{ fontSize: 13, color: "#1890ff", fontWeight: 500 }}
                >
                  <QuestionCircleOutlined style={{ marginRight: 6 }} />
                  How to use this page
                </span>
              ),
              children: (
                <Steps
                  direction="vertical"
                  // size="small"
                  style={{ padding: "8px 12px" }}
                  items={[
                    {
                      title: "Review suggested dividers",
                      description:
                        "We've pre-detected section changes based on the drawing index. Blue lines in the PDF indicate where sections start.",
                      status: "process",
                      // icon: <AimOutlined />,
                    },
                    {
                      title: "Rename sections as needed",
                      description:
                        'Click the edit (pencil) icon on any divider to rename it — e.g. "Architectural", "Electrical", "Plumbing".',
                      status: "process",
                      // icon: <EditOutlined />,
                    },
                    {
                      title: "Add or remove dividers",
                      description:
                        'Use the "+ Add divider after page X" buttons between pages to split sections further. Use the trash icon to merge sections.',
                      status: "process",
                      // icon: <ScissorOutlined />,
                    },
                    {
                      title: "Confirm Sections",
                      description:
                        'When the sections look right, click "Confirm Sections" at the top right.',
                      status: "process",
                      // icon: <CheckOutlined />,
                    },
                  ]}
                />
              ),
            },
          ]}
        />
      </div>

      <div className={styles.wrapper}>
        <div className={styles.mainView}>
          {numPages && (
            <div className={styles.controlsWrapper}>
              <div className={styles.zoomControls}>
                <Button
                  type="primary"
                  shape="circle"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.5}
                >
                  <ZoomOutOutlined />
                </Button>
                <span className={styles.zoomLevel}>
                  {Math.round(zoomLevel * 100)}%
                </span>
                <Button
                  type="primary"
                  shape="circle"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 4}
                >
                  <ZoomInOutlined />
                </Button>
              </div>
            </div>
          )}

          <div
            className={styles.pdfContainer}
            ref={pdfContainerRef}
            onMouseDown={handleMouseDown}
            style={{
              cursor: containerCursor,
              position: "relative",
            }}
          >
            {pageLoading && (
              <div className={styles.spinner}>
                <div className={styles.spinnerCircle}></div>
              </div>
            )}

            <div
              className={styles.pageWrapper}
              style={{ width: pageWidth, position: "relative" }}
              ref={pageRef}
            >
              {fileURL && (
                <Document
                  file={fileURL}
                  onLoadSuccess={onLoadSuccess}
                  onLoadError={onLoadError}
                  loading={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "400px",
                        gap: 16,
                      }}
                    >
                      <div className={styles.spinnerCircle}></div>
                      <div>Loading PDF...</div>
                    </div>
                  }
                >
                  {numPages &&
                    Array.from(new Array(numPages), (el, index) => {
                      const pageNumber = index + 1;
                      const divider = getDividerForPage(pageNumber);
                      const hasDivider = !!divider;
                      const dividerIndex = divider
                        ? dividers.findIndex((d) => d.id === divider.id)
                        : -1;

                      const sectionEndPage =
                        dividerIndex < dividers.length - 1
                          ? dividers[dividerIndex + 1].beforePage - 1
                          : numPages;

                      return (
                        <div key={`page_wrapper_${pageNumber}`}>
                          {/* Divider line with section name - SIMPLE DESIGN */}
                          {hasDivider && (
                            <div
                              ref={(el) =>
                                (dividerRefs.current[divider.id] = el)
                              }
                              data-divider-control="true"
                              style={{
                                margin: "24px 0",
                                position: "relative",
                                maxWidth: `${maxDividerWidth}px`,
                                width: "100%",
                              }}
                            >
                              {/* Top border line */}
                              <div
                                style={{
                                  height: "2px",
                                  background: "#1890ff",
                                  marginBottom: "8px",
                                }}
                              />

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 12,
                                  padding: "8px 0",
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  {editingDivider === divider.id ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: "center",
                                      }}
                                    >
                                      <Input
                                        value={editingName}
                                        onChange={(e) =>
                                          setEditingName(e.target.value)
                                        }
                                        onPressEnter={() =>
                                          handleSaveEdit(divider.id)
                                        }
                                        autoFocus
                                        size="small"
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 600,
                                          flex: 1,
                                        }}
                                      />
                                      <Button
                                        type="primary"
                                        size="small"
                                        icon={<CheckOutlined />}
                                        onClick={() =>
                                          handleSaveEdit(divider.id)
                                        }
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="small"
                                        icon={<CloseOutlined />}
                                        onClick={handleCancelEdit}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: 12,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 15,
                                          fontWeight: 600,
                                          color: "#262626",
                                        }}
                                      >
                                        {divider.name}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 13,
                                          color: "#8c8c8c",
                                        }}
                                      >
                                        Pages {pageNumber} - {sectionEndPage}
                                      </span>
                                      {divider.isCustom && (
                                        <span
                                          style={{
                                            fontSize: 11,
                                            padding: "2px 8px",
                                            background: "#f0f0f0",
                                            color: "#595959",
                                            borderRadius: 3,
                                            fontWeight: 500,
                                          }}
                                        >
                                          Custom
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {editingDivider !== divider.id && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 6,
                                    }}
                                  >
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<EditOutlined />}
                                      onClick={() => handleEditDivider(divider)}
                                    />
                                    <Popconfirm
                                      title="Remove this divider?"
                                      description="This will merge this section with the previous one."
                                      onConfirm={() =>
                                        handleRemoveDivider(divider.id)
                                      }
                                      okText="Remove"
                                      cancelText="Cancel"
                                    >
                                      <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                      />
                                    </Popconfirm>
                                  </div>
                                )}
                              </div>

                              {/* Bottom border line */}
                              <div
                                style={{
                                  height: "1px",
                                  background: "#e8e8e8",
                                  marginTop: "8px",
                                }}
                              />
                            </div>
                          )}

                          <div style={{ position: "relative" }}>
                            <Page
                              pageNumber={pageNumber}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              width={pageWidth}
                              onLoadSuccess={onPageLoadSuccess}
                              loading={
                                <div className={styles.pagePlaceholder}>
                                  <div className={styles.spinnerCircle}></div>
                                </div>
                              }
                            />

                            {/* Add divider button below every page */}
                            <div
                              data-divider-control="true"
                              style={{
                                position: "absolute",
                                bottom: -20,
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 10,
                              }}
                            >
                              <Button
                                type="dashed"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => handleAddDivider(pageNumber + 1)}
                                style={{
                                  borderRadius: 20,
                                  fontSize: 11,
                                  padding: "2px 12px",
                                  height: 28,
                                }}
                                disabled={
                                  pageNumber === numPages ||
                                  pageNumber === sectionEndPage
                                }
                              >
                                Add divider after page {pageNumber}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </Document>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar showing sections with active highlighting */}
        <div
          style={{
            width: 320,
            backgroundColor: "#f8f9fa",
            borderLeft: "1px solid #e0e0e0",
            overflowY: "auto",
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <FileTextOutlined style={{ fontSize: 18, color: "#1890ff" }} />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
              Document Sections
            </h3>
          </div>

          {sectionsData.length === 0 ? (
            <div
              style={{
                padding: 20,
                textAlign: "center",
                color: "#999",
                fontSize: 14,
              }}
            >
              No sections defined yet. Add dividers to create sections.
            </div>
          ) : (
            sectionsData.map((section, index) => (
              <div
                key={index}
                onClick={() => handleSectionClick(section)}
                style={{
                  backgroundColor:
                    activeSection === index ? "#e6f4ff" : "white",
                  borderRadius: 8,
                  padding: 16,
                  boxShadow:
                    activeSection === index
                      ? "0 2px 8px rgba(24, 144, 255, 0.2)"
                      : "0 1px 3px rgba(0,0,0,0.1)",
                  border:
                    activeSection === index
                      ? "2px solid #1890ff"
                      : "2px solid transparent",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== index) {
                    e.currentTarget.style.boxShadow =
                      "0 2px 6px rgba(0,0,0,0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== index) {
                    e.currentTarget.style.boxShadow =
                      "0 1px 3px rgba(0,0,0,0.1)";
                  }
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 15,
                    marginBottom: 8,
                    color: activeSection === index ? "#0050b3" : "#1890ff",
                  }}
                >
                  {section.name}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#666",
                    marginBottom: 12,
                  }}
                >
                  Pages {section.startPage} - {section.endPage}
                </div>

                {section.pages.length > 0 && (
                  <div
                    style={{
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: 12,
                    }}
                  >
                    {section.pages.map((page, pageIndex) => (
                      <div
                        key={pageIndex}
                        style={{
                          fontSize: 12,
                          color: "#555",
                          marginBottom: 6,
                          paddingLeft: 8,
                          borderLeft: "2px solid #e8e8e8",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 500,
                            color: "#888",
                            marginRight: 6,
                          }}
                        >
                          P{page.page}:
                        </span>
                        {page.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DividePDF;
